/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 *
 * Not: Panel UI dil listesi sabittir ve uydularda da derleme zamanında gerekir.
 * Bu nedenle `apps/api/src/reference-data` (Core-only) bağımlılığı kaldırılmıştır;
 * sabitler bu dosya içinde inline tanımlanır. Geniş ISO 639-1 listesi runtime'da
 * Core'un `/api/reference/languages/world` endpoint'inden tüketilir.
 */

/**
 * Desteklenen panel UI dilleri. Bu liste hem Core hem de uydularda derleme
 * zamanında bilinmelidir; runtime'da değişmez.
 */
export const SUPPORTED_LANGUAGE_CODES = [
  'tr',
  'en',
  'ar',
  'de',
  'fr',
  'es',
  'it',
  'ru',
  'zh',
  'ja',
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGE_CODES)[number];

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [...SUPPORTED_LANGUAGE_CODES];

export const NATIVE_LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  tr: 'Türkçe',
  en: 'English',
  ar: 'العربية',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  ru: 'Русский',
  zh: '中文',
  ja: '日本語',
};

/** Default application locale (single source of truth; must be one of SUPPORTED_LANGUAGES). */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'tr';

/** Default secondary locale when one is excluded (e.g. auto-translate target). */
export function getDefaultSecondaryLocale(excludeLocale?: string): SupportedLanguage {
  const other = SUPPORTED_LANGUAGES.find((l) => l !== excludeLocale);
  return other ?? DEFAULT_LANGUAGE;
}

/**
 * Resolve language from browser. Returns a supported locale code; fallback is DEFAULT_LANGUAGE.
 */
export function getBrowserLanguage(): SupportedLanguage {
  try {
    const browserLang = navigator.language || (navigator as any).userLanguage || DEFAULT_LANGUAGE;
    const langCode = browserLang.toLowerCase().split('-')[0] as SupportedLanguage;
    return SUPPORTED_LANGUAGES.includes(langCode) ? langCode : DEFAULT_LANGUAGE;
  } catch (e) {
    return DEFAULT_LANGUAGE;
  }
}

/**
 * Dil kodunu normalize et
 */
export function normalizeLanguage(lang: string | null | undefined): SupportedLanguage {
  if (!lang) return DEFAULT_LANGUAGE;
  const normalized = lang.toLowerCase().trim().split('-')[0] as SupportedLanguage;
  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : DEFAULT_LANGUAGE;
}
